// src/components/GlobalDesignBar.tsx
import React from "react";
import { Box, Group, TextInput, ActionIcon, Tooltip, Divider } from "@mantine/core";
import { FaSearch, FaRuler } from "react-icons/fa";
import { FiMaximize2, FiHelpCircle } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  setMeasurementToolActiveInRedux,
  setMouseHelpModalOpenInRedux,
  setRequestZoomExtentInRedux,
} from "../designSlice";
import { RotationControl } from "./RotationControl";
import { BuildingStoryMenu } from "./BuildingStoryMenu";
import { RoomColoringMenu } from "../../RoomColoring/RoomColoringMenu";
import { DragSelectRoomsControl } from "./DragSelectRoomsControl";
import { MouseHelpModal } from "../../components/MouseHelpModal";
import { ComponentsMenu } from "./ComponentsMenu/ComponentsMenu";
import { ModuleSelector } from "./ModuleSelector";
import { ViewerFreeTextSearch } from "./ViewerFreeTextSearch";

export const GlobalDesignBar: React.FC = () => {
  const dispatch = useAppDispatch();

  const activeModule = useAppSelector((s) => s.design.activeModule);
  const dragSelectSpaces = useAppSelector((s) => s.design.dragSelectSpaces);
  const measurementToolActive = useAppSelector((s) => s.design.measurementToolActive);
  const mouseHelpModalOpen = useAppSelector((s) => s.design.mouseHelpModalOpen);

  return (
    <>
      <Box
        bg="white"
        px="md"
        py={6}
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-3)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          zIndex: 10,
        }}
      >
        {mouseHelpModalOpen && (
          <MouseHelpModal
            opened={mouseHelpModalOpen}
            onClose={() => dispatch(setMouseHelpModalOpenInRedux(false))}
          />
        )}
        <Group justify="space-between" align="center" gap="md">
          {/* ===== VENSTRE: etasje + modul-kontekst ===== */}
          <Group gap="sm" align="center">
            <ModuleSelector />

            <Divider orientation="vertical" h={24} />
            <BuildingStoryMenu />
            <Divider orientation="vertical" h={24} />
            <ComponentsMenu />
            <Divider orientation="vertical" h={24} />
          </Group>

          {/* ===== HØYRE: søk + verktøy for visningen ===== */}
          <Group gap="sm" align="center">
            {activeModule !== "EXPLORE" && (
              <>
                <Divider orientation="vertical" h={24} />
                <RoomColoringMenu />
              </>
            )}
            <Divider orientation="vertical" h={24} />

            <ViewerFreeTextSearch width={260} />
            <Divider orientation="vertical" h={24} />

            {/* Blokk 3: rom-verkøy */}
            <DragSelectRoomsControl />

            <Tooltip label="Zoom extent" position="bottom" withArrow>
              <ActionIcon
                variant="subtle"
                size={34}
                radius="xl"
                onClick={() => dispatch(setRequestZoomExtentInRedux())}
              >
                <FiMaximize2 size={16} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Mål avstand" position="bottom" withArrow>
              <ActionIcon
                variant="subtle"
                size={34}
                radius="xl"
                disabled={measurementToolActive || dragSelectSpaces}
                onClick={() => dispatch(setMeasurementToolActiveInRedux(true))}
              >
                <FaRuler size={16} />
              </ActionIcon>
            </Tooltip>

            <Divider orientation="vertical" h={24} />

            {/* Blokk 4: rotasjon */}
            <RotationControl />

            <Divider orientation="vertical" h={24} />

            {/* ✅ Hjelp (musekontroller) - lengst til høyre */}
            <Tooltip label="Musekontroller" position="bottom" withArrow>
              <ActionIcon
                variant="subtle"
                size={34}
                radius="xl"
                aria-label="Musekontroller"
                onClick={() => dispatch(setMouseHelpModalOpenInRedux(true))}
              >
                <FiHelpCircle size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Box>
    </>
  );
};
